import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { CloseOutlined, SaveOutlined } from "@mui/icons-material";
import { Alert, Autocomplete, CircularProgress, Divider, Drawer, IconButton, TextField, Tooltip } from "@mui/material";

import { useCreateRoleAssignmentMutation } from "state/rtkQuery/access/accessMutations";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import { formatRoleLabel } from "utils/accessManagement/roleDisplay";
import { isValidEmail } from "utils/accessManagement/roleAssignmentForm";
import { showToast } from "utils/reusableToast";

import "pages/ecrOps/OrgEditDrawer.css";

// Org-scoped assignment drawer: org_id is fixed to the current org (never
// exposed as an editable field) and only org-scoped roles are offered, so an
// Org Owner can grant Organization Owner/Approver access without touching
// platform-wide RBAC. Always a user assignment — application identities are
// managed on the platform-wide Access Management surface.
const OrgAssignRoleDrawer = ({ open, orgId, orgName, orgRoles, onClose, onCreated }) => {
  const currentUserEmail = useSelector((state) => state.auth?.userAccount?.username || "");
  const [roleId, setRoleId] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [createAssignment, { isLoading, isError, error, reset }] = useCreateRoleAssignmentMutation();

  useEffect(() => {
    if (open) {
      setRoleId("");
      setEmail("");
      setErrors({});
      reset();
    }
  }, [open]);

  const selectedRole = orgRoles.find((r) => r.id === roleId) || null;

  const validate = () => {
    const e = {};
    if (!roleId) e.roleId = "Role is required.";
    if (!email.trim()) {
      e.email = "Email is required.";
    } else if (!isValidEmail(email)) {
      e.email = "Enter a valid email address.";
    }
    return e;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await createAssignment({
        role_id: roleId,
        email: email.trim(),
        // org_id is a list on this endpoint; this drawer is locked to one org.
        org_id: [orgId],
        is_app: false,
        assigned_by: currentUserEmail,
        is_active: true,
      }).unwrap();
      showToast({ title: "Role assigned", description: `${email.trim()} was granted ${formatRoleLabel(selectedRole?.role_name)}.`, type: "success" });
      onCreated();
    } catch (err) {
      const fieldErrors = err?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        setErrors((e) => ({ ...e, ...fieldErrors }));
      }
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 440 } } }}>
      <div className="org-drawer__header">
        <div className="org-drawer__header-text">
          <p className="org-drawer__header-eyebrow">Org Access</p>
          <h2 className="org-drawer__header-title">Assign role</h2>
        </div>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af" }}>
            <CloseOutlined />
          </IconButton>
        </Tooltip>
      </div>

      <div className="org-drawer__body">
        <div className="org-drawer__section">
          <div className="org-drawer__fields">
            <Autocomplete
              options={orgRoles}
              getOptionLabel={(role) => formatRoleLabel(role.role_name)}
              value={selectedRole}
              onChange={(e, role) => { setRoleId(role?.id || ""); setErrors((er) => ({ ...er, roleId: undefined })); }}
              renderInput={(params) => (
                <TextField {...params} label="Role" required size="small" error={!!errors.roleId} helperText={errors.roleId} />
              )}
            />
            <TextField
              label="Email"
              required
              size="small"
              fullWidth
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors((er) => ({ ...er, email: undefined })); }}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField label="Organization" size="small" fullWidth value={orgName || orgId} InputProps={{ readOnly: true }} helperText="Locked to this organization." />
          </div>
        </div>

        {isError && (
          <div className="org-drawer__section">
            <Alert severity="error" sx={{ fontSize: "13px" }}>
              {extractApiErrorMessage(error, "Failed to create role assignment. Please try again.")}
            </Alert>
          </div>
        )}

        <Divider />
        <div className="org-drawer__section">
          <p style={{ fontSize: "12px", color: "#9ca3af" }}>
            Only organization-scoped roles are available here. Platform-wide roles (Enterprise Admin, Operations Admin)
            are managed by platform administrators.
          </p>
        </div>
      </div>

      <div className="org-drawer__footer">
        <button type="button" className="org-drawer__btn org-drawer__btn--cancel" onClick={onClose} disabled={isLoading}>
          Cancel
        </button>
        <button type="button" className="org-drawer__btn org-drawer__btn--save" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> : <SaveOutlined style={{ fontSize: 16, marginRight: 6 }} />}
          {isLoading ? "Assigning…" : "Assign role"}
        </button>
      </div>
    </Drawer>
  );
};

OrgAssignRoleDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  orgId: PropTypes.string.isRequired,
  orgName: PropTypes.string,
  orgRoles: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default OrgAssignRoleDrawer;
